import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useFamilyContext } from '../../../../contexts/FamilyContext';

const ParentPortalContext = createContext();

export const useParentPortal = () => {
  const context = useContext(ParentPortalContext);
  if (!context) {
    throw new Error('useParentPortal must be used within a ParentPortalProvider');
  }
  return context;
};

export const ParentPortalProvider = ({ children, value: externalValue }) => {
  const { currentUser, familyUsers } = useFamilyContext();

  // Use external values if provided, otherwise maintain internal state
  const [selectedStudent, setSelectedStudent] = useState(
    externalValue?.selectedStudent || null
  );
  const [studentJourneyPhase, setStudentJourneyPhase] = useState(
    externalValue?.studentJourneyPhase || 'discovery'
  );

  const [studentProgress, setStudentProgress] = useState(null);
  const [sharedItems, setSharedItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get available students (filter for students specifically)
  const students = familyUsers?.filter(user => user.type === 'student') || [];

  // Load student progress data
  const loadStudentProgress = async (student) => {
    if (!student) return;

    setLoading(true);
    try {
      // Load various progress indicators
      const [
        collegesResult,
        essaysResult,
        milestonesResult,
        assessmentResult
      ] = await Promise.all([
        // Colleges added
        supabase
          .from('college_journey_colleges')
          .select('*')
          .eq('user_id', student.auth_user_id || student.id),

        // Essays written
        supabase
          .from('college_journey_essays')
          .select('*')
          .eq('user_id', student.auth_user_id || student.id),

        // Milestones completed
        supabase
          .from('college_journey_milestones')
          .select('*')
          .eq('user_id', student.auth_user_id || student.id),

        // Assessment completion
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', student.auth_user_id || student.id)
      ]);

      const progress = {
        colleges: collegesResult.data || [],
        essays: essaysResult.data || [],
        milestones: milestonesResult.data || [],
        hasAssessment: (assessmentResult.data && assessmentResult.data.length > 0),
        totalMilestones: 12, // This could be dynamic
        completedMilestones: (milestonesResult.data || []).filter(m => m.completed).length
      };

      // Determine journey phase based on progress
      let phase = 'discovery';
      if (progress.hasAssessment && progress.colleges.length > 0) {
        phase = 'planning';
      }
      if (progress.essays.length > 0 || progress.completedMilestones > 8) {
        phase = 'execution';
      }

      setStudentProgress(progress);
      setStudentJourneyPhase(phase);

    } catch (error) {
      console.error('Error loading student progress:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load shared items for the selected student
  const loadSharedItems = async (student) => {
    if (!student || !currentUser) return;

    try {
      // Get parent's family member record
      const { data: parentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!parentMember) return;

      // Get items shared with this parent by the selected student
      const { data: permissions } = await supabase
        .from('sharing_permissions')
        .select(`
          *,
          student:family_members!student_id(
            id,
            name,
            auth_user_id
          )
        `)
        .eq('shared_with_id', parentMember.id)
        .eq('student.auth_user_id', student.auth_user_id || student.id)
        .is('revoked_at', null)
        .order('shared_at', { ascending: false });

      setSharedItems(permissions || []);
    } catch (error) {
      console.error('Error loading shared items:', error);
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    if (!currentUser) return;

    try {
      const { data: parentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!parentMember) return;

      const { data: notifs } = await supabase
        .from('sharing_notifications')
        .select('*')
        .eq('recipient_id', parentMember.id)
        .order('created_at', { ascending: false })
        .limit(10);

      setNotifications(notifs || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Update selected student
  const updateSelectedStudent = (student) => {
    if (externalValue?.setSelectedStudent) {
      externalValue.setSelectedStudent(student);
    } else {
      setSelectedStudent(student);
    }

    if (student) {
      localStorage.setItem('parent-portal-selected-student', student.id);
      loadStudentProgress(student);
      loadSharedItems(student);
    }
  };

  // Auto-load data when student changes
  useEffect(() => {
    const student = externalValue?.selectedStudent || selectedStudent;
    if (student) {
      loadStudentProgress(student);
      loadSharedItems(student);
    }
  }, [externalValue?.selectedStudent, selectedStudent, currentUser]);

  // Auto-select student if only one exists
  useEffect(() => {
    if (students.length === 1 && !selectedStudent) {
      const savedStudentId = localStorage.getItem('parent-portal-selected-student');
      const studentToSelect = savedStudentId
        ? students.find(s => s.id === savedStudentId) || students[0]
        : students[0];
      updateSelectedStudent(studentToSelect);
    }
  }, [students]);

  // Load notifications on mount
  useEffect(() => {
    if (currentUser) {
      loadNotifications();
    }
  }, [currentUser]);

  const contextValue = {
    // Student management
    students,
    selectedStudent: externalValue?.selectedStudent || selectedStudent,
    setSelectedStudent: updateSelectedStudent,

    // Progress tracking
    studentJourneyPhase: externalValue?.studentJourneyPhase || studentJourneyPhase,
    studentProgress,

    // Data
    sharedItems,
    notifications,
    loading,

    // Methods
    loadStudentProgress,
    loadSharedItems,
    loadNotifications,
    refreshData: () => {
      const student = externalValue?.selectedStudent || selectedStudent;
      if (student) {
        loadStudentProgress(student);
        loadSharedItems(student);
        loadNotifications();
      }
    }
  };

  return (
    <ParentPortalContext.Provider value={contextValue}>
      {children}
    </ParentPortalContext.Provider>
  );
};